// Snakes & Ladders — engine & data.
// Classic 10x10 board, squares numbered 1..100.
// Player rolls 1-6. Landing on a ladder bottom → answer correctly to climb.
// Landing on a snake head → answer correctly to escape biting (else slide down).
// Knowledge squares = free learning + 10 pts.
// Boss squares (50, 75) = 3-question rapid round.
// First to 100 wins (must roll EXACTLY — overshoot bounces back).

export const BOARD_SIZE = 10;
export const TOTAL_CELLS = 100;

// Classic ladder bottom → top mappings
export const LADDERS = { 2: 23, 8: 34, 20: 77, 28: 84, 36: 44, 50: 91, 71: 92 };

// Snake head → tail mappings (head higher, tail lower)
export const SNAKES = { 99: 41, 95: 67, 89: 53, 74: 32, 62: 19, 49: 11, 46: 25, 16: 6 };

export const KNOWLEDGE_SQUARES = [3, 13, 33, 43, 63, 73, 83, 97];
export const BOSS_SQUARES = [50, 75];

// Cell number → (row, col) in a 10x10 grid where row 0 = top.
// Standard SAL: bottom-left is 1, snakes goes right ↑ in zig-zag.
// Row 9 (bottom): cells 1-10 (L→R)
// Row 8: cells 11-20 (R→L)
// Row 7: cells 21-30 (L→R), etc.
export function cellCoord(n) {
    if (n < 1 || n > TOTAL_CELLS) return null;
    const idx = n - 1;
    const rowFromBottom = Math.floor(idx / BOARD_SIZE);
    const row = BOARD_SIZE - 1 - rowFromBottom; // top-indexed
    const colInRow = idx % BOARD_SIZE;
    const goingRight = rowFromBottom % 2 === 0;
    const col = goingRight ? colInRow : BOARD_SIZE - 1 - colInRow;
    return [row, col];
}

export function squareType(n) {
    if (LADDERS[n] !== undefined) return 'ladder';
    if (SNAKES[n] !== undefined) return 'snake';
    if (BOSS_SQUARES.includes(n)) return 'boss';
    if (KNOWLEDGE_SQUARES.includes(n)) return 'knowledge';
    return 'plain';
}

// Bot answer probability
export function botAnswersCorrectly() { return Math.random() < 0.65; }

export const TOPIC_PRESETS = [
    'Propulse FR/FRLS house wiring safety',
    'XLPE LT & HT power cables',
    'IS 694 / IS 7098 / IS 1554 standards',
    'Electrical site safety, PPE & LOTO',
    'Cable drum handling & counterfeit detection',
    'Channel partner billing & inventory ops',
    'Electrician troubleshooting basics',
];

export const FALLBACK_QUESTIONS = [
    { question: 'Which Indian Standard governs PVC insulated domestic wires?', options: ['IS 1554', 'IS 694', 'IS 7098', 'IS 9968'], correct: 'IS 694', explanation: 'IS 694 covers PVC insulated cables up to 1100V.' },
    { question: 'What does FRLS stand for?', options: ['Fire-Resistant Low Surge', 'Flame Retardant Low Smoke', 'Fully Recyclable Lead-free', 'Flexible Rated Long Service'], correct: 'Flame Retardant Low Smoke', explanation: 'FRLS wires release minimal smoke and resist flame spread.' },
    { question: 'Earth wire colour as per IS 11353?', options: ['Red', 'Black', 'Green / Green-Yellow', 'Blue'], correct: 'Green / Green-Yellow', explanation: 'Green/green-yellow striped is the universal earth identifier.' },
    { question: 'XLPE vs PVC — XLPE allows higher?', options: ['Colour options', 'Operating temperature', 'Flexibility', 'Cost'], correct: 'Operating temperature', explanation: 'XLPE handles 90°C continuous vs PVC 70°C.' },
    { question: 'PPE for live LV panel work?', options: ['Sunglasses', 'Insulated gloves & arc shield', 'T-shirt', 'Slippers'], correct: 'Insulated gloves & arc shield', explanation: 'Class-0 insulated gloves & arc-rated shields are mandatory.' },
    { question: 'Best cable drum storage?', options: ['Flat on side', 'Upright on flanges, dry', '10 stacked', 'Outdoors uncovered'], correct: 'Upright on flanges, dry', explanation: 'Upright on flanges in covered, dry storage prevents moisture ingress.' },
    { question: 'XLPE LT cable rated up to?', options: ['440V', '1.1 kV', '11 kV', '33 kV'], correct: '1.1 kV', explanation: 'LT XLPE cables are rated up to 1.1 kV.' },
    { question: 'Conductor for premium house wires?', options: ['Aluminium', '99.97% electrolytic copper', 'Steel', 'Brass'], correct: '99.97% electrolytic copper', explanation: 'Electrolytic-grade copper has lowest resistivity.' },
    { question: 'LOTO stands for?', options: ['Line-Out Test-On', 'Lockout-Tagout', 'Low-Tension Output', 'Load-Off Time-Out'], correct: 'Lockout-Tagout', explanation: '6-step procedure to prevent accidental re-energisation.' },
    { question: 'Minimum IR for new house wiring?', options: ['0.1 MΩ', '0.5 MΩ', '1 MΩ', '50 MΩ'], correct: '1 MΩ', explanation: '≥1 MΩ measured with 500V megger.' },
    { question: 'Counterfeit cable risk?', options: ['Higher cost', 'Lower copper purity → overheating', 'Bright colours', 'Longer life'], correct: 'Lower copper purity → overheating', explanation: 'Counterfeits often use copper-clad aluminium.' },
    { question: 'Cable size for 16A AC point?', options: ['0.5 sq.mm', '1.0 sq.mm', '2.5 sq.mm', '4.0 sq.mm'], correct: '4.0 sq.mm', explanation: '4 sq.mm copper for 16A circuits.' },
];

export const LEARNING_TIPS = [
    { title: 'FR vs FRLS vs FR-LSH', body: 'FR retards flame. FRLS adds low smoke. FR-LSH is low smoke + halogen-free — safest for hospitals & data centres.' },
    { title: 'IS 694 in 30 seconds', body: 'IS 694 covers single-core PVC wires up to 1100V — the backbone standard for Indian house wiring.' },
    { title: 'Cable Ampacity Rule', body: '2.5 sq.mm copper carries ~24A in open air, ~18A in conduit. Derate for grouping & ambient temperature.' },
    { title: 'Insulation Resistance Test', body: 'Use a 500V megger. Reading must be ≥1 MΩ between phase-neutral, phase-earth and neutral-earth.' },
    { title: 'Drum Storage', body: 'Store drums upright on flanges in dry covered space. Lying flat can crush inner layers.' },
    { title: 'Counterfeit Detection', body: 'Test conductor with a magnet — pure copper is non-magnetic. CCA cuts unevenly and feels lighter.' },
    { title: 'LOTO 6 Steps', body: 'Notify → Identify → Isolate → Lockout/Tagout → Verify zero energy → Test before touch.' },
];

export const COLOR_THEMES = {
    yellow: { primary: '#FFF200', dark: '#B8860B', text: '#231F20', name: 'You' },
    red:    { primary: '#E31E24', dark: '#9F1212', text: '#FFFFFF', name: 'Bot Red' },
};
